import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, GeoJSON, Tooltip, Popup, useMapEvents } from "react-leaflet";
import L from "leaflet";
import api from "@/api";
import { useNoctua } from "./NoctuaContext";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, MapPin, AlertCircle, Layers, Map as MapIcon, Hash, X } from "lucide-react";
import { ResultBreakdown, winnerInfo } from "@/components/noctua/ResultBreakdown";

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Hook component that exposes current map zoom to parent via callback
function ZoomTracker({ onZoom }) {
  useMapEvents({
    zoomend: (e) => onZoom(e.target.getZoom()),
  });
  return null;
}

// LOD threshold: importancia required to be visible at a given zoom
// Default state zoom = 6. As user zooms in, less-important municipios appear.
const lodMinImportancia = (zoom) => {
  if (zoom <= 6) return 8;   // top ~20 (importancia 8-10)
  if (zoom <= 7) return 6;   // top ~40-50
  if (zoom <= 8) return 4;   // top ~70%
  return 1;                   // all
};

export default function MapaPage() {
  const { partidos, clientParty, selectedState, setSelectedState, allStates: ctxStates, scopeFilter } = useNoctua();
  const [allStates, setAllStates] = useState([]);
  const [stateMunicipios, setStateMunicipios] = useState({}); // {estado_clave: count}
  const estadoClave = Number(selectedState);
  const setEstadoClave = (v) => setSelectedState(v);
  const [anio, setAnio] = useState(2024);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedMuni, setSelectedMuni] = useState(null);
  const [viewMode, setViewMode] = useState("municipal"); // municipal | seccional
  const [sectionItems, setSectionItems] = useState([]);
  const [seedingState, setSeedingState] = useState(false);
  const [mapZoom, setMapZoom] = useState(6);
  const [selectedSec, setSelectedSec] = useState(null);
  const geoParams = anio === 2027 ? {} : { params: { anio } };
  const win = (r) => winnerInfo(r, partyColor);

  const partyColor = (clave) => partidos.find((p) => p.clave === clave)?.color || "#64748B";

  const isMunicipalScope = scopeFilter?.scope_type === "municipal" && scopeFilter?.municipio_clave;
  const isDistrictScope = scopeFilter?.scope_type === "distrital_federal" || scopeFilter?.scope_type === "distrital_local";
  const districtLoaded = isDistrictScope && scopeFilter?.district_mapping_loaded && (scopeFilter.district_municipios_claves?.length > 0);

  useEffect(() => {
    if (ctxStates && ctxStates.length) setAllStates(ctxStates);
    else api.get("/catalog/states").then((r) => setAllStates(r.data));
  }, [ctxStates]);

  useEffect(() => {
    setLoading(true);
    setSelectedMuni(null);
    setSelectedSec(null);
    setMapZoom(6);  // reset LOD when changing state
    api.get(`/noctua/geojson/estado/${estadoClave}`, geoParams)
      .then((r) => setFeatures(r.data.features || []))
      .finally(() => setLoading(false));
    if (viewMode === "seccional") {
      api.get(`/noctua/secciones?estado_clave=${estadoClave}&anio=${anio}&limit=5000`)
        .then((r) => setSectionItems(r.data.items || []))
        .catch(() => setSectionItems([]));
    }
  }, [estadoClave, anio, viewMode]);

  const seedCurrentState = async () => {
    setSeedingState(true);
    try {
      await api.post(`/noctua/municipios/seed-state/${estadoClave}`);
      // Rescatter so dots distribute across the state
      await api.post(`/noctua/municipios/rescatter/${estadoClave}`).catch(() => {});
      const r = await api.get(`/noctua/geojson/estado/${estadoClave}`, geoParams);
      setFeatures(r.data.features || []);
    } finally {
      setSeedingState(false);
    }
  };

  const filtered = useMemo(() => {
    let out = features;
    // Scope filter: municipal → only the assigned municipio
    if (isMunicipalScope) {
      out = out.filter((f) => Number(f.properties?.municipio_clave) === Number(scopeFilter.municipio_clave));
    } else if (districtLoaded) {
      const allowed = new Set(scopeFilter.district_municipios_claves.map(Number));
      out = out.filter((f) => allowed.has(Number(f.properties?.municipio_clave)));
    }
    const q = search.trim().toLowerCase();
    if (!q) return out;
    return out.filter((f) =>
      (f.properties?.nombre || "").toLowerCase().includes(q)
    );
  }, [features, search, isMunicipalScope, districtLoaded, scopeFilter]);

  const sectionPoints = useMemo(() => {
    const byMuni = new Map(filtered.map((f) => [f.properties?.id, f.properties]));
    const groups = {};
    sectionItems.forEach((s) => {
      if (byMuni.get(s.municipio_id)?.lat) (groups[s.municipio_id] = groups[s.municipio_id] || []).push(s);
    });
    const golden = Math.PI * (3 - Math.sqrt(5));
    return Object.entries(groups).flatMap(([mid, secs]) => {
      const m = byMuni.get(mid);
      return secs
        .sort((a, b) => Number(a.seccion) - Number(b.seccion))
        .map((s, k) => {
          const r = 0.005 * Math.sqrt(k + 0.5);
          return { s, lat: m.lat + r * Math.cos(k * golden), lng: m.lng + r * 1.1 * Math.sin(k * golden) };
        });
    }).slice(0, 1500);
  }, [sectionItems, filtered]);

  const topSectionWinners = useMemo(() => {
    const acc = {};
    sectionPoints.forEach(({ s }) => {
      const w = winnerInfo(s, partyColor);
      if (w) acc[w.label] = (acc[w.label] || 0) + 1;
    });
    return Object.entries(acc).sort((a, b) => b[1] - a[1]).slice(0, 3);
  }, [sectionPoints]); // eslint-disable-line

  const withCentroid = filtered.filter((f) => f.properties?.lat && f.properties?.lng);
  const withGeom = filtered.filter((f) => f.geometry);

  // LOD: filter visible markers by importancia rank vs current zoom
  const visibleByLod = useMemo(() => {
    const minImp = lodMinImportancia(mapZoom);
    return withCentroid.filter((f) => (f.properties?.importancia ?? 5) >= minImp);
  }, [withCentroid, mapZoom]);

  const hiddenCount = withCentroid.length - visibleByLod.length;

  const radius = (f) => {
    const pct = f.properties?.ganador_pct;
    if (!pct) return 5;
    // Scale 6→13 based on winning %; bounded so dense states stay legible
    return Math.max(5, Math.min(13, 5 + (pct - 30) * 0.35));
  };

  const center = useMemo(() => {
    const lats = withCentroid.map((f) => f.properties.lat);
    const lngs = withCentroid.map((f) => f.properties.lng);
    if (!lats.length) return [23.6345, -102.5528];
    return [
      lats.reduce((a, b) => a + b, 0) / lats.length,
      lngs.reduce((a, b) => a + b, 0) / lngs.length,
    ];
  }, [withCentroid]);

  const styleFeature = (f) => ({
    fillColor: win(f.properties)?.color || "#1e293b",
    fillOpacity: f.properties.ganador ? 0.55 : 0.15,
    color: "rgba(148, 163, 184, 0.4)",
    weight: 1,
  });

  return (
    <div className="space-y-6" data-testid="noctua-mapa-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-blue-400">Operación nacional</div>
          <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl text-white">Mapa Electoral</h1>
          <p className="text-sm text-slate-400 mt-1">
            32 estados · datos históricos 2018/2021/2024 + predicción ML 2027
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* View mode toggle */}
          <div className="flex bg-slate-900 border border-slate-700 rounded-md overflow-hidden" data-testid="mapa-mode-toggle">
            <button
              onClick={() => setViewMode("municipal")}
              className={`px-3 py-1.5 text-xs flex items-center gap-1.5 ${viewMode === "municipal" ? "bg-blue-600 text-white" : "text-slate-300 hover:text-white"}`}
              data-testid="mapa-mode-municipal"
            >
              <MapIcon className="w-3.5 h-3.5" /> Municipal
            </button>
            <button
              onClick={() => setViewMode("seccional")}
              className={`px-3 py-1.5 text-xs flex items-center gap-1.5 ${viewMode === "seccional" ? "bg-blue-600 text-white" : "text-slate-300 hover:text-white"}`}
              data-testid="mapa-mode-seccional"
            >
              <Hash className="w-3.5 h-3.5" /> Seccional
            </button>
          </div>
          <Select value={String(estadoClave)} onValueChange={(v) => setEstadoClave(parseInt(v, 10))}>
            <SelectTrigger className="w-52 h-9 bg-slate-900 border-slate-700 text-white" data-testid="mapa-state-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 max-h-80">
              {allStates.map((s) => (
                <SelectItem key={s.clave} value={String(s.clave)} className="text-white" data-testid={`state-opt-${s.clave}`}>
                  {String(s.clave).padStart(2, "0")} · {s.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(anio)} onValueChange={(v) => setAnio(parseInt(v, 10))}>
            <SelectTrigger className="w-32 h-9 bg-slate-900 border-slate-700 text-white" data-testid="mapa-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {[2018, 2021, 2024, 2027].map((y) => (
                <SelectItem key={y} value={String(y)} className="text-white">{y}{y === 2027 ? " (pred.)" : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar…"
              className="h-9 pl-9 pr-3 bg-slate-900 border border-slate-700 rounded-md text-sm text-white w-44"
              data-testid="mapa-search"
            />
          </div>
        </div>
      </div>

      {anio === 2027 && (
        <div className="noctua-card p-3 border-l-4 border-l-blue-500 flex items-center gap-2 text-sm text-slate-200">
          <AlertCircle className="w-4 h-4 text-blue-400" />
          Visualizando <strong className="text-blue-400">predicción ML 2027</strong>. Selecciona municipio para ver detalle del modelo (R² por partido).
        </div>
      )}

      {isDistrictScope && !districtLoaded && (
        <div className="noctua-card p-3 border-l-4 border-l-amber-500 flex items-start gap-2 text-sm text-slate-200" data-testid="distrital-cartografia-banner">
          <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
          <div>
            <strong className="text-amber-400">Cartografía distrital pendiente.</strong>{" "}
            La subcuenta está adscrita al{" "}
            <strong>{scopeFilter?.scope_type === "distrital_federal" ? "Distrito Federal" : "Distrito Local"} {scopeFilter?.distrito_clave}</strong> de {scopeFilter?.entidad_nombre}.
            Mientras el Super Admin no cargue el mapping distrito→municipios INE, se muestra el estado completo como referencia.
          </div>
        </div>
      )}

      {isDistrictScope && districtLoaded && (
        <div className="noctua-card p-3 border-l-4 border-l-emerald-500 flex items-center gap-2 text-sm text-slate-200 flex-wrap" data-testid="distrital-loaded-banner">
          <MapPin className="w-4 h-4 text-emerald-400" />
          Vista restringida al{" "}
          <strong className="text-emerald-300">
            {scopeFilter?.scope_type === "distrital_federal" ? "Distrito Federal" : "Distrito Local"} #{scopeFilter?.distrito_clave}
          </strong>
          <span className="text-slate-400">·</span>
          <span className="text-slate-300">{scopeFilter.district_municipios_claves.length} municipios · {scopeFilter?.entidad_nombre}</span>
        </div>
      )}

      {isMunicipalScope && (
        <div className="noctua-card p-3 border-l-4 border-l-emerald-500 flex items-center gap-2 text-sm text-slate-200" data-testid="municipal-scope-banner">
          <MapPin className="w-4 h-4 text-emerald-400" />
          Vista restringida al municipio{" "}
          <strong className="text-emerald-300 ml-1">{scopeFilter?.municipio_nombre || `#${scopeFilter?.municipio_clave}`}</strong>
          <span className="ml-1 text-slate-400">· {scopeFilter?.entidad_nombre}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 noctua-card overflow-hidden relative" style={{ height: "560px" }}>
          {!loading && features.length > 0 && hiddenCount > 0 && viewMode === "municipal" && (
            <div className="absolute top-3 right-3 z-[1000] bg-slate-900/90 backdrop-blur-sm border border-slate-700 rounded-md px-3 py-1.5 text-[11px] text-slate-300 flex items-center gap-1.5 pointer-events-none" data-testid="lod-hint">
              <Layers className="w-3 h-3 text-blue-400" />
              <span>{visibleByLod.length} de {withCentroid.length} mostrados · <span className="text-blue-400">zoom +</span> para ver más</span>
            </div>
          )}
          {loading ? (
            <div className="h-full grid place-items-center text-slate-400">Cargando…</div>
          ) : features.length === 0 ? (
            <EmptyMapState onSeed={seedCurrentState} loading={seedingState} stateName={allStates.find((s) => s.clave === estadoClave)?.nombre} />
          ) : (
            <MapContainer
              key={`${estadoClave}-${features.length}-${viewMode}-${isMunicipalScope || withCentroid.length === 1 ? "muni" : districtLoaded ? "dist" : "state"}`}
              center={center}
              zoom={isMunicipalScope || withCentroid.length === 1 ? 12 : districtLoaded ? 9 : 6}
              style={{ width: "100%", height: "100%" }}
              data-testid="mapa-leaflet"
            >
              <TileLayer
                attribution="Tiles &copy; Esri &mdash; Esri, HERE, Garmin, &copy; OpenStreetMap contributors"
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                maxZoom={16}
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}"
                maxZoom={16}
              />
              <ZoomTracker onZoom={setMapZoom} />
              {viewMode === "municipal" && withGeom.map((f) => (
                <GeoJSON
                  key={f.properties.id + "-poly"}
                  data={f}
                  style={() => styleFeature(f)}
                  onEachFeature={(_, layer) => {
                    layer.bindTooltip(f.properties.nombre || "", { sticky: true });
                    layer.on("click", () => setSelectedMuni(f.properties));
                  }}
                />
              ))}
              {viewMode === "municipal" && visibleByLod.map((f) => (
                <CircleMarker
                  key={f.properties.id}
                  center={[f.properties.lat, f.properties.lng]}
                  radius={radius(f)}
                  pathOptions={{
                    color: win(f.properties)?.color || "#60a5fa",
                    fillColor: win(f.properties)?.color || "#60a5fa",
                    fillOpacity: 0.7,
                    weight: 1,
                  }}
                  eventHandlers={{ click: () => setSelectedMuni(f.properties) }}
                >
                  <Tooltip>
                    <div className="text-xs">
                      <div className="font-bold">{f.properties.nombre}</div>
                      {f.properties.ganador ? (
                        <div>{win(f.properties).label} · {win(f.properties).pct}%</div>
                      ) : (
                        <div className="text-slate-400">Sin datos</div>
                      )}
                    </div>
                  </Tooltip>
                </CircleMarker>
              ))}
              {viewMode === "seccional" && sectionPoints.map(({ s, lat, lng }) => {
                const w = win(s);
                const active = selectedSec?.id === s.id;
                return (
                  <CircleMarker
                    key={`sec-${s.id}`}
                    center={[lat, lng]}
                    radius={active ? 10 : isMunicipalScope || withCentroid.length === 1 ? 8 : 5}
                    pathOptions={{
                      color: active ? "#ffffff" : w?.color || "#60a5fa",
                      fillColor: w?.color || "#60a5fa",
                      fillOpacity: 0.85,
                      weight: active ? 2 : 1,
                    }}
                    eventHandlers={{ click: () => setSelectedSec(s) }}
                  >
                    <Tooltip>
                      <div className="text-xs">
                        <div className="font-bold">Sección {s.seccion}</div>
                        <div className="text-slate-400">{s.municipio_nombre}</div>
                        {w && <div>{w.label} · {w.pct}%</div>}
                      </div>
                    </Tooltip>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          )}
        </div>

        {viewMode === "municipal" ? (
          <InfoPanel selected={selectedMuni} anio={anio} clientParty={clientParty} partidos={partidos} win={win} />
        ) : (
          selectedSec ? (
            <SectionDetail sec={selectedSec} win={win} onClose={() => setSelectedSec(null)} />
          ) : (
            <SectionsSummary items={sectionPoints.map((p) => p.s)} win={win} onPick={setSelectedSec} />
          )
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        {viewMode === "municipal" ? (
          <>
            <Pill label={isMunicipalScope ? "Municipio" : "Municipios"} value={filtered.length} />
            <Pill label="Con datos" value={filtered.filter((f) => f.properties.ganador).length} />
            <Pill label="Con geometría" value={filtered.filter((f) => f.geometry).length} />
            <Pill label="Sin información" value={filtered.filter((f) => !f.properties.ganador).length} accent="red" />
          </>
        ) : (
          <>
            <Pill label="Secciones cargadas" value={sectionPoints.length} />
            {topSectionWinners.map(([label, n]) => <Pill key={label} label={label} value={n} />)}
          </>
        )}
      </div>
    </div>
  );
}

function Pill({ label, value, accent }) {
  return (
    <div className="noctua-card p-3 flex items-center justify-between gap-2 min-w-0">
      <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 truncate" title={label}>{label}</span>
      <span className={`font-display font-bold text-lg tabular-nums ${accent === "red" ? "text-red-400" : "text-white"}`}>
        {value.toLocaleString()}
      </span>
    </div>
  );
}

function EmptyMapState({ onSeed, loading, stateName }) {
  return (
    <div className="h-full grid place-items-center text-center p-8">
      <div>
        <MapPin className="w-12 h-12 mx-auto text-slate-600" />
        <h3 className="mt-4 font-display font-bold text-xl text-white">Catálogo vacío</h3>
        <p className="mt-2 text-sm text-slate-400 max-w-md">
          No hay municipios sembrados para <strong className="text-blue-300">{stateName || "este estado"}</strong>.
          Puedes sembrarlos automáticamente desde el EDMSLM del INE.
        </p>
        <Button onClick={onSeed} disabled={loading} className="mt-4 bg-blue-600 hover:bg-blue-700" data-testid="mapa-seed-state-button">
          {loading ? "Sembrando…" : `Sembrar ${stateName || "estado"}`}
        </Button>
      </div>
    </div>
  );
}

function SectionsSummary({ items, win, onPick }) {
  const byWinner = items.reduce((acc, s) => {
    const w = win(s);
    if (w) {
      acc[w.label] = acc[w.label] || { n: 0, color: w.color };
      acc[w.label].n += 1;
    }
    return acc;
  }, {});
  const sorted = Object.entries(byWinner).sort((a, b) => b[1].n - a[1].n);
  const showList = items.length > 0 && items.length <= 60;
  return (
    <div className="noctua-card p-5" data-testid="sections-summary">
      <div className="text-[10px] uppercase tracking-[0.25em] text-blue-400">Vista seccional</div>
      <h2 className="mt-1 font-display font-black text-xl text-white">Secciones ganadas</h2>
      {items.length === 0 ? (
        <div className="mt-6 text-sm text-slate-400">
          Sin resultados seccionales cargados. Ve a <strong className="text-blue-400">Datos</strong> para
          importar un CSV con resultados por sección.
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {sorted.map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 text-xs" data-testid={`sections-summary-row-${k}`}>
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: v.color }} />
              <span className="w-28 truncate text-slate-300" title={k}>{k}</span>
              <div className="flex-1 h-2 bg-slate-800 rounded overflow-hidden">
                <div className="h-full" style={{ width: `${(v.n / items.length) * 100}%`, backgroundColor: v.color }} />
              </div>
              <span className="text-white w-14 text-right tabular-nums">{v.n} secs.</span>
            </div>
          ))}
        </div>
      )}
      {showList && (
        <div className="mt-4 pt-3 border-t border-slate-800">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">Toca una sección para ver su detalle</div>
          <div className="grid grid-cols-4 gap-1.5 max-h-44 overflow-y-auto" data-testid="sections-list">
            {[...items].sort((a, b) => Number(a.seccion) - Number(b.seccion)).map((s) => (
              <button
                key={s.id}
                onClick={() => onPick(s)}
                className="text-[11px] px-1.5 py-1 rounded bg-slate-800/70 hover:bg-slate-700 text-slate-200 flex items-center gap-1 transition-colors"
                data-testid={`section-pick-${s.seccion}`}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: win(s)?.color }} />
                {s.seccion}
              </button>
            ))}
          </div>
        </div>
      )}
      <p className="mt-4 text-[10px] text-slate-500">
        Las secciones se muestran agrupadas alrededor del centroide de su municipio (visualización demo).
        Para mapeo exacto, sube el GeoJSON seccional del INE en Datos.
      </p>
    </div>
  );
}

function SectionDetail({ sec, win, onClose }) {
  const w = win(sec);
  return (
    <div className="noctua-card p-5" data-testid="section-detail">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-blue-400">{sec.municipio_nombre} · {sec.anio}</div>
          <h2 className="mt-1 font-display font-black text-xl text-white" data-testid="section-detail-title">Sección {sec.seccion}</h2>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="section-detail-close">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="mt-4 space-y-2 text-sm">
        {w && <Row k="Ganador" v={<span className="font-bold text-white" data-testid="section-detail-winner">{w.label} <span className="text-blue-400">{w.pct}%</span></span>} />}
        {w && <Row k="Margen" v={`${w.margen} pts`} />}
        <Row k="Lista nominal" v={sec.lista_nominal?.toLocaleString() || "—"} />
        <Row k="Participación" v={`${sec.participacion_pct || 0}%`} />
      </div>
      <ResultBreakdown result={sec} testPrefix="section-breakdown" />
    </div>
  );
}

function InfoPanel({ selected, anio, clientParty, partidos, win }) {
  const [tab, setTab] = useState("historico");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    setDetail(null);
    if (!selected?.id || !selected.anio) return;
    api.get(`/noctua/municipios/${selected.id}`)
      .then((r) => setDetail(r.data.resultados.find((x) => x.anio === selected.anio) || null))
      .catch(() => setDetail(null));
  }, [selected]);
  const [prediccion, setPrediccion] = useState(null);
  const [predLoading, setPredLoading] = useState(false);
  const [desgaste, setDesgaste] = useState(0);

  const runPredict = (id, dst = desgaste) => {
    setPredLoading(true);
    api.post(`/noctua/predict?municipio_id=${id}&desgaste_pct=${dst}`)
      .then((r) => setPrediccion(r.data))
      .catch(() => setPrediccion(null))
      .finally(() => setPredLoading(false));
  };

  useEffect(() => {
    setPrediccion(null);
    if (selected && anio === 2027) {
      setTab("prediccion");
      runPredict(selected.id, desgaste);
    } else if (selected) {
      setTab("historico");
    }
  }, [selected, anio]); // eslint-disable-line

  if (!selected) {
    return (
      <div className="noctua-card p-6 text-center text-slate-400 flex items-center justify-center min-h-[280px]">
        <div>
          <MapPin className="w-8 h-8 mx-auto mb-2 text-slate-600" />
          Selecciona un municipio en el mapa para ver detalle.
        </div>
      </div>
    );
  }

  return (
    <div className="noctua-card p-5" data-testid="info-panel">
      <div className="text-[10px] uppercase tracking-[0.25em] text-blue-400">Municipio</div>
      <h2 className="mt-1 font-display font-black text-xl text-white">{selected.nombre}</h2>
      <div className="mt-4 flex gap-3 text-xs">
        <button
          className={`px-3 py-1.5 rounded ${tab === "historico" ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-300"}`}
          onClick={() => setTab("historico")}
          data-testid="info-tab-historico"
        >
          Histórico
        </button>
        <button
          className={`px-3 py-1.5 rounded ${tab === "prediccion" ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-300"}`}
          onClick={() => { setTab("prediccion"); if (!prediccion) runPredict(selected.id); }}
          data-testid="info-tab-prediccion"
        >
          Predicción ML 2027
        </button>
      </div>

      <div className="mt-4 space-y-2 text-sm">
        {tab === "historico" && (
          <>
            {selected.ganador ? (
              <>
                <Row k="Ganador" v={<span className="font-bold text-white" data-testid="info-panel-winner">{win(selected).label} <span className="text-blue-400">{win(selected).pct}%</span></span>} />
                <Row k="Margen" v={`${win(selected).margen} pts`} />
                <Row k="Lista nominal" v={selected.lista_nominal?.toLocaleString() || "—"} />
                <Row k="Participación" v={`${selected.participacion_pct || 0}%`} />
                <Row k="Año" v={selected.anio || "—"} />
                <ResultBreakdown result={detail} testPrefix="muni-breakdown" />
              </>
            ) : (
              <div className="text-slate-400 italic">Sin resultados cargados para este municipio.</div>
            )}
          </>
        )}
        {tab === "prediccion" && (
          predLoading ? <div className="text-slate-400">Calculando ML…</div> :
          !prediccion ? <div className="text-slate-400 italic">Sin histórico suficiente para predecir.</div> :
          <>
            <div className="flex items-center justify-between text-xs mb-2 pb-2 border-b border-slate-800">
              <span className="text-slate-400">Desgaste incumbencia</span>
              <div className="flex items-center gap-1.5">
                <input
                  type="range"
                  min={0}
                  max={15}
                  value={desgaste}
                  onChange={(e) => setDesgaste(parseInt(e.target.value, 10))}
                  onMouseUp={() => runPredict(selected.id, desgaste)}
                  onTouchEnd={() => runPredict(selected.id, desgaste)}
                  className="w-24"
                  data-testid="desgaste-slider"
                />
                <span className="text-blue-400 font-bold w-10 text-right">{desgaste} pts</span>
              </div>
            </div>
            <Row k="Ganador predicho" v={<span className="font-bold text-white">{prediccion.ganador_predicho} <span className="text-blue-400">{prediccion.porcentajes_prediccion[prediccion.ganador_predicho]}%</span></span>} />
            <Row k="Margen predicho" v={`${prediccion.margen_predicho} pts`} />
            <Row k="Votos estimados" v={prediccion.votos_estimados.toLocaleString()} />
            <Row k="Lista nom. proy." v={prediccion.lista_nominal_estimada.toLocaleString()} />
            <Row k="Modelo" v={<span className="text-xs">{prediccion.modelo?.tipo} · {prediccion.modelo?.n_observaciones} obs.</span>} />
            <div className="pt-2 border-t border-slate-800 mt-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1">Distribución predicha + R²</div>
              {Object.entries(prediccion.porcentajes_prediccion)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5)
                .map(([k, v]) => {
                  const r2 = prediccion.modelo?.r2_por_partido?.[k];
                  return (
                    <div key={k} className="flex items-center gap-2 text-xs my-1">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: partidos.find((p) => p.clave === k)?.color }} />
                      <span className="w-12 text-slate-300">{k}</span>
                      <div className="flex-1 h-1.5 bg-slate-800 rounded overflow-hidden">
                        <div className="h-full" style={{ width: `${v}%`, backgroundColor: partidos.find((p) => p.clave === k)?.color }} />
                      </div>
                      <span className="text-white tabular-nums w-12 text-right">{v}%</span>
                      <span className="text-slate-500 tabular-nums w-12 text-right text-[10px]" title="Coeficiente de determinación">
                        R²={r2 ?? "—"}
                      </span>
                    </div>
                  );
                })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-slate-400 shrink-0">{k}</span>
      <span className="text-slate-100 text-right">{v}</span>
    </div>
  );
}

